import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { Redirect, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import Audience from './Audience';
import Programmatic from './Programmatic';
import Administrator from './Administrator';
import { stores } from '../stores';
import { Sidebar } from '../components/Sidebar/Sidebar';
import { TopBarContent } from '../components/TopBar/TopBarContent';
import { TopBarProvider } from '../components/TopBar/TopBarProvider';
import { ReportsProvider } from '../providers/ReportsProvider/ReportsProvider';
import TitleProvider from '../providers/TitleProvider/TitleProvider';
import { getProductType, PRODUCT_TYPE } from '../lib/access';
import BrowserUtils from '../lib/browserUtils';
import { Smarts } from '../components/Smarts/Smarts';
import { useAiStore } from '../stores/AiStore';
import SystemData from '../lib/systemData';
import { Toast } from '../components/Toast/Toast';
import { Link } from '../components/Link/Link';
import DelayedCacheInvalidation from '../lib/delayedCacheInvalidation';

export default function Main({ children }) {
	// Audience or yield
	// Only relevant for non-admin users that has both audience and yield (programmatic).
	const location = useLocation();
	const productType = getProductType();
	const { buildInfo } = SystemData.genericData;

	if (location.pathname.startsWith('/public')) {
		return null;
	}
	if (!stores.identity.value.authenticated) {
		if (location.pathname !== '/login' && location.pathname !== '/') {
			const to = BrowserUtils.makeQs('/login', {
				redirect: location.pathname + location.search,
			});
			return <Redirect to={to} />;
		}
		const STAY_PARTS = ['isSuperadminGoogleVerification', 'redirect'];
		if (location.pathname === '/login' && STAY_PARTS.some((p) => location.search.includes(p))) {
			return <Redirect to={`${location.pathname}${location.search}`} />;
		}
		return <Redirect to="/login" />;
	}

	// used when generating PDFs in report subscriptions
	const mainFullScreen = location.hash?.includes('mainFullScreen');

	return (
		<>
			<ReportsProvider productType={productType}>
				<TitleProvider>
					<TopBarProvider>
						<div className="flex flex-row w-full h-full min-h-screen">
							{/* HACK: Fixed elements that extends the background colour of the sidebar and main content out into the Safari/MacOS/Mobile elastic overscroll */}
							<OverscrollBackground />

							<div className="flex flex-col w-full h-full min-w-0 z-0">
								{!mainFullScreen && <TopBarContent />}

								<div className="flex flex-row w-full h-full grow z-0">
									{/* Navigation sidebar */}
									{!mainFullScreen && <Sidebar productType={productType} />}

									{/* Main content */}
									<main className="relative w-full min-w-0 bg-bg-page -z-[1] overflow-visible">
										{
											productType === 'admin' && (
												<Administrator>
													{children}
												</Administrator>
											)
										}
										{
											productType === 'full' && (
												<Programmatic
													productType="full"
												>
													{children}
													<Audience>
														<Redirect to="/dashboard" />
													</Audience>
												</Programmatic>
											)
										}
										{
											productType === 'programmatic' && (
												<Programmatic>
													{children}
													<Redirect to="/dashboard" />
												</Programmatic>
											)
										}
										{
											productType === 'audience' && (
												<Audience includeReportRoutes>
													{children}
													<Redirect to="/audience/dashboard" />
												</Audience>
											)
										}
									</main>
								</div>
							</div>

							<SmartsPlaceholder />
						</div>
						{SystemData.genericData.aiEnabled && (SystemData.genericData.aiAdminOnly === false || stores.identity.isAdministrator()) && <Smarts />}
						<DelayedCacheInvalidation />
						{buildInfo && (
							<Toast
								open
								message={(
									<div className="flex flex-col gap-1">
										<span className="font-bold">Build Info</span>
										<div className="flex flex-col">
											{buildInfo.link && (
												<Link href={buildInfo.link.href} title={buildInfo.link.text} className="truncate">{buildInfo.link.text}</Link>
											)}
											{buildInfo.commit && (
												<span className="truncate" title={buildInfo.commit}>{buildInfo.commit}</span>
											)}
											{buildInfo.author && (
												<span className="truncate" title={buildInfo.author}>{buildInfo.author}</span>
											)}
											{buildInfo.date && (
												<span className="truncate" title={buildInfo.date}>{buildInfo.date}</span>
											)}
										</div>
									</div>
								)}
								status="info"
								icon={null}
								position="bottom-left"
								// inline
							/>
						)}
					</TopBarProvider>
				</TitleProvider>
			</ReportsProvider>
		</>
	);
}

Main.propTypes = {
	children: PropTypes.oneOfType([
		PropTypes.arrayOf(PropTypes.node),
		PropTypes.node,
	]),
};

Main.defaultProps = {
	children: null,
};

function SmartsPlaceholder() {
	const {
		isPinnedAndOpen: isAiPinnedAndOpen,
		pinnedWidth: aiPinnedWidth,
	} = useAiStore();

	return (
		<div
			id="main-ai-placeholder"
			className="flex-none transition-all duration-400"
			style={{
				width: isAiPinnedAndOpen ? 'var(--ai-width)' : 0,
				'--ai-width': `${aiPinnedWidth}px`,
			}}
		/>
	);
}

function OverscrollBackground() {
	const topRef = useRef(null);

	useEffect(() => {
		const handleScroll = () => {
			if (topRef.current) {
				topRef.current.style.visibility = document.scrollingElement.scrollTop <= 0 ? 'visible' : 'hidden';
			}
		};
		document.addEventListener('scroll', handleScroll);
		handleScroll();
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	return (
		<>
			{/* Sidebar and page background overscroll */}
			<div className="inset-0 fixed top-0 left-0 w-0 sm:w-(--sidebar-sm) lg:w-(--sidebar-lg) h-[100vh] bg-component-navbar-bg-primary" />
			<div className="fixed top-0 left-0 sm:left-(--sidebar-sm) lg:left-(--sidebar-lg) right-0 h-[100vh] bg-bg-page" />
			{/* Top bar overscroll, only visible when scroll is at the top of the page, so it isn't visible if an absolute element stretches the page beyond the page height */}
			<div ref={topRef} className="fixed top-0 left-0 right-0 h-[50vh] bg-bg-static-surface_default" />
		</>
	);
}
